/* eslint-disable @typescript-eslint/no-shadow */
import dayjs, { Dayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Connection } from 'mysql2/promise';
import { v4 } from 'uuid';
import { FeatureFlags } from '../constants/flags';
import { ACTIVITY_CHANGE_TYPE, STRING_BOOLEAN_HASH } from '../constants/globals';
import { ChiroUpDayJsCommon } from '../constants/stringConstants';
import { CaseSubType } from '../enums/CaseSubType.enum';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { DisplayDataRecord } from '../functions/normalizeDisplayData';
import { createDayjs } from '../functions/time';
import { TransactionMagicEvent } from '../services/businessRules.service/transactionMagicCommon';
import { AppointmentClass } from './Appointment.class';
import { AppointmentInsuranceType, AppointmentStatuses } from './Appointment.type';
import { CodeSets } from './BillingCode.type';
import { Invoice } from './Invoice.type';
import { PatientPackage } from './Packages.type';
import { Patient } from './Patient.type';
import { Insurance } from './PatientInsurance.type';
import { PatientNote } from './PatientNote.type';
import {
  PatientPayment,
  PaymentType,
  PayorPaymentTypesHash,
  TransactionPayment,
} from './PatientPayment.type';
import { Payor, PayorLineItemType } from './Payor.type';

dayjs.extend(utc);
dayjs.extend(tz);

export enum TransactionListUsage {
  PatientPurchases = 'patient-purchases',
  BillingLedgerPurchases = 'billing-ledger-purchases',
}

export type TransactionPayorCodeType = {
  code: string;
  allowedAmount: number;
  payorId: number;
  replacementCode?: string | null;
};

export type ConsolidatedTransactionGetResponse = {
  items: PatientTransaction[];
  policies?: Insurance[];
  group?: any[];
  patient: Patient;
  memos?: { data: PatientNote[]; skip?: number; limit?: number };
  patientPackages?: {
    packages: PatientPackage[];
    servicesCovered: { [key: number]: number[] };
    treatmentsCovered: { [key: number]: number[] };
  };
};

export type PatientBalanceTransferProps = {
  amount?: number;
  referenceNumber?: string;
  provider?: {
    id: string;
    displayName: string;
    supervisedId?: string;
    supervisedName?: string;
  };
  date?: string | undefined;
  description?: string;
  locationId?: number;
};

export type CreditTransactionsNeedUpdatedType = {
  billingKey: string;
  item: PatientTransactionItemType;
};

/**
 * This is the minimal information to build a transaction.
 */
export type TransactionPatientSummaryType = {
  id: string;
  status: AppointmentStatuses;
  billingKey?: string;
  clinicId?: number;
  locationId?: number;
  appointmentDate?: Date | Dayjs | number | string;
  fname: string;
  lname: string;
  pname?: string;
  phone: string;
  email: string;
  displayName: string;
  clinician?: {
    id?: string;
    name?: string;
    supervisedId?: string;
    supervisedName?: string;
  };
  profilePhoto?: string;
  dateOfBirth?: string;
  accountNumber?: string;
  treatmentId?: number;
  disciplineId?: number;
};

export enum TransactionTypeEnum {
  Hybrid = 'Hybrid',
  Encounter = 'Encounter',
  AdHoc = 'AdHoc',
  Payment = 'Payment',
  Credit = 'Credit',
}

export const BillableTransactionType = {
  [TransactionTypeEnum.Encounter]: true,
  [TransactionTypeEnum.Hybrid]: true,
};

export const BillableTransactionTypes = Object.keys(BillableTransactionType);

export const isaPurchaseTransaction = (t: PatientTransaction) => {
  return t.type !== TransactionTypeEnum.Payment && t.type !== TransactionTypeEnum.Credit;
};

/**
 * [8746744786 by Brian] Added a guard so this does not run for
 * canceled or no-show appointments. Some of the "magic" rules
 * might flub up the transactions otherwise.
 *
 * @param t
 * @returns
 */
export const isaTreatmentTransactionType = (t: PatientTransaction) => {
  return (
    (t.type === TransactionTypeEnum.Encounter || t.type === TransactionTypeEnum.Hybrid) &&
    t.status !== AppointmentStatuses.Canceled &&
    t.status !== AppointmentStatuses.NoShow
  );
};

export const isaDebitTransactionType = (t: PatientTransaction) => {
  return (
    t.type === TransactionTypeEnum.Hybrid ||
    t.type === TransactionTypeEnum.AdHoc ||
    t.type === TransactionTypeEnum.Encounter
  );
};

export enum TransactionPurchaseSubtypeEnum {
  BalanceTransfer = 'BalanceTransfer',
  PurchasePackage = 'PurchasePackage',
}

export type TransactionPatientBriefSummaryType = Partial<
  Omit<TransactionPatientSummaryType, 'status'>
>;

export type ProviderOnTransactionType = {
  id: string;
  displayName: string;
  supervisedId?: string;
  supervisedName?: string;
  primaryId?: string | null;
};

export enum TransactionAdjustmentTypeEnum {
  ReversePaymentLineItem = 'ReversePaymentLineItem',
}

export type TransactionAdjustmentType = {
  ts: number;
  user: { id: string; displayName: string };
  items: {
    type: TransactionAdjustmentTypeEnum;
    description: string;
    data: any;
  }[];
};

export type TransactionSummaryType = {
  notes?: string[];
  items?: string[];
  deletedItems?: Partial<PatientTransactionItemType>[];
  adjustments?: TransactionAdjustmentType[];
  briefItems?: string[];
  lock?: {
    postClaim?: PostClaimLock;
  };
};

export type PostClaimLockEntry = {
  idempotencyKey: string;
  timestamp: number; // Unix milliseconds
  userId: number;
  userName: string;
};

export type PostClaimLock = {
  inProgress: Record<string, PostClaimLockEntry>;
  completed: Record<string, PostClaimLockEntry>;
};

export type TransactionActionType = {
  id?: string | null;
  ts?: number | null;
  displayName?: string | null;
};

export enum TransactionCreditMethodEnum {
  Cash = 'Cash',
  Check = 'Check',
  Credit = 'Credit',
  ACH = 'ACH',
}

/**
 * [2025-08-22.0845 by Brian] There are only TWO write-off enums, however,
 * there are three types of write offs (#writeoff).
 *
 *   1) Patient write-off: subtype === PatientWriteOff
 *   2) Payor write-off: subtype === PayorWriteOff && payorId is not null
 *   3) Purchase write-off: subtype === PayorWriteOff && payorId is null
 */
export enum TransactionItemSubtypeEnum {
  AdditionalFee = 'AdditionalFee',
  Adjustment = 'Adjustment',
  AdjustmentPackage = 'AdjustmentPackage', // This subtype is a working subtype used in tax calculation but should never actually be written to the database as an item subtype
  AppliedPayment = 'AppliedPayment',
  BalanceAllocatedToPatient = 'BalanceAllocatedToPatient',
  BalanceTransfer = 'BalanceTransfer',
  Canceled = 'Canceled',
  IncentivePayment = 'IncentivePayment',
  NoShow = 'NoShow',
  Override = 'Override',
  Package = 'Package',
  PatientCredit = 'PatientCredit',
  PatientPayment = 'PatientPayment',
  PatientRefund = 'PatientRefund',
  PatientService = 'PatientService',
  PatientWriteOff = 'WriteOff',
  Payment = 'Payment',
  PayorWriteOff = 'PayorWriteOff',
  PayorWriteOffReversal = 'PayorWriteOffReversal',
  ProcessingFee = 'ProcessingFee',
  RefundUnappliedPayment = 'RefundUnappliedPayment',
  Reprocessed = 'Reprocessed',
  Service = 'Service',
  Supplement = 'Supplement',
  Tax = 'Tax',
  Treatment = 'Treatment',
  AddTreatment = 'AddTreatment',
}

export const paymentSubtypes = [
  TransactionItemSubtypeEnum.PatientPayment,
  TransactionItemSubtypeEnum.Payment,
  TransactionItemSubtypeEnum.AppliedPayment,
];

export const TransactionItemSubtypeWriteOffs = {
  [TransactionItemSubtypeEnum.PatientWriteOff]: TransactionItemSubtypeEnum.PatientWriteOff,
  [TransactionItemSubtypeEnum.PayorWriteOff]: TransactionItemSubtypeEnum.PayorWriteOff,
};
export const TransactionItemSubtypeAdjustments = {
  [TransactionItemSubtypeEnum.Adjustment]: TransactionItemSubtypeEnum.Adjustment,
  [TransactionItemSubtypeEnum.Override]: TransactionItemSubtypeEnum.Override,
};

export const TransactionItemSubtypePayments = {
  [TransactionItemSubtypeEnum.Payment]: TransactionItemSubtypeEnum.Payment,
  [TransactionItemSubtypeEnum.PatientPayment]: TransactionItemSubtypeEnum.PatientPayment,
  [TransactionItemSubtypeEnum.AppliedPayment]: TransactionItemSubtypeEnum.AppliedPayment,
} as { [key: string]: string };

export const TransactionItemSubtypeTax = {
  [TransactionItemSubtypeEnum.Tax]: TransactionItemSubtypeEnum.Tax,
};

export const TransactionItemSubtypeService = {
  [TransactionItemSubtypeEnum.Service]: TransactionItemSubtypeEnum.Service,
  [TransactionItemSubtypeEnum.PatientService]: TransactionItemSubtypeEnum.PatientService,
} as { [key: string]: string };

export const isaTaxItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Tax;
};

export const notaTaxItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaTaxItem(item);
};

export const isaReprocessedItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Reprocessed;
};

/**
 * Currently excludes:
 * - Void items (voided transactions should not count toward balance)
 * - Tax items (handled separately in tax calculations)
 */
export const isaBalanceCalculationExcludedItem = (
  item: PatientTransactionItemType | null | undefined
) => {
  if (!item) return false;

  return (
    item.type === TransactionItemTypeEnum.Void || item.subtype === TransactionItemSubtypeEnum.Tax
  );
};

export const isaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return !!TransactionItemSubtypeService[item.subtype];
};

export const isaPackageApplied = (items: PatientTransactionItemType[] | null | undefined) => {
  if (!items) return false;
  if (!Array.isArray(items)) return false;
  if (items.length === 0) return false;

  return items.some(
    item => item?.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId
  );
};

export const isaPackageItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId;
};

const TreatmentOrServiceSet = new Set([
  TransactionItemSubtypeEnum.Service,
  TransactionItemSubtypeEnum.PatientService,
  TransactionItemSubtypeEnum.Treatment,
]);

export const isaTreatmentOrServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.type === TransactionItemTypeEnum.Debit && TreatmentOrServiceSet.has(item.subtype);
};

export const isaSupplementItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Supplement;
};

const statementItemDebitSubtypes = {
  [TransactionItemSubtypeEnum.Canceled]: true,
  [TransactionItemSubtypeEnum.NoShow]: true,
  [TransactionItemSubtypeEnum.Package]: true,
  [TransactionItemSubtypeEnum.Supplement]: true,
  [TransactionItemSubtypeEnum.BalanceTransfer]: true,
} as STRING_BOOLEAN_HASH;

export const isaStatementDebitItem = (item: PatientTransactionItemType | null | undefined) => {
  return statementItemDebitSubtypes?.[item?.subtype ?? '_'] === true;
};

export const notaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaServiceItem(item);
};

export const isaPaymentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return Boolean(TransactionItemSubtypePayments[item.subtype]);
};

export const isaPatientPaymentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;

  return item?.type === TransactionItemTypeEnum.Credit && isaPaymentItem(item) && !item.payorId;
};

export const isaProductItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item?.subtype === TransactionItemSubtypeEnum.Supplement;
};

export const isaTreatmentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return (
    item.subtype === TransactionItemSubtypeEnum.Treatment ||
    item.subtype === TransactionItemSubtypeEnum.AddTreatment
  );
};

export const isaDebitItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type) return false;

  return item.type === TransactionItemTypeEnum.Debit;
};

/**
 * Items that can be replaced/offset by Override items.
 * Only specific debit subtypes should be offset when overrides are applied.
 * Excludes items like Reprocessed (purchase reversals) which are paired with payments.
 */
export const isaOverridableItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type || !item.subtype) return false;

  return (
    item.type === TransactionItemTypeEnum.Debit &&
    (item.subtype === TransactionItemSubtypeEnum.Service ||
      item.subtype === TransactionItemSubtypeEnum.PatientService ||
      item.subtype === TransactionItemSubtypeEnum.Supplement ||
      item.subtype === TransactionItemSubtypeEnum.Treatment ||
      item.subtype === TransactionItemSubtypeEnum.AddTreatment)
  );
};

export const isaWriteOffItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type) return false;
  if (!item.subtype) return false;

  return (
    item.type === TransactionItemTypeEnum.Credit &&
    (item.subtype === TransactionItemSubtypeEnum.PatientWriteOff ||
      item.subtype === TransactionItemSubtypeEnum.PayorWriteOff)
  );
};

export const isaPurchaseWriteOffItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.type) return false;
  if (!item.subtype) return false;

  return item.subtype === TransactionItemSubtypeEnum.PayorWriteOff;
};

export const isaBriefDescriptionItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return (
    item.subtype === TransactionItemSubtypeEnum.Treatment ||
    item.subtype === TransactionItemSubtypeEnum.AddTreatment ||
    item.subtype === TransactionItemSubtypeEnum.Supplement ||
    item.subtype === TransactionItemSubtypeEnum.Package ||
    item.subtype === TransactionItemSubtypeEnum.NoShow ||
    item.subtype === TransactionItemSubtypeEnum.Canceled
  );
};

export const isaAddedTreatmentItem = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item.subtype === TransactionItemSubtypeEnum.AddTreatment;
};

export const isAPackageApplied = (item: PatientTransactionItemType | null | undefined) => {
  if (!item) return false;
  if (!item.subtype) return false;

  return item.subtype === TransactionItemSubtypeEnum.Adjustment && !!item?.packageId;
};

export const isNotaServiceItem = (item: PatientTransactionItemType | null | undefined) => {
  return !isaServiceItem(item);
};

// Any debit item that is a service item.
export const isaInsuranceBillableItem = (item: PatientTransactionItemType | null | undefined) => {
  return (
    item?.type === TransactionItemTypeEnum.Debit &&
    item?.subtype === TransactionItemSubtypeEnum.Service
  );
};

// Any debit item that is not a service item or tax.
export const isNotaInsuranceBillableItem = (
  item: PatientTransactionItemType | null | undefined
) => {
  return (
    item?.type === TransactionItemTypeEnum.Debit &&
    item?.subtype !== TransactionItemSubtypeEnum.Service &&
    item?.subtype !== TransactionItemSubtypeEnum.Tax
  );
};

export const isNotaInsuranceBillableItemOrTax = (
  item: PatientTransactionItemType | null | undefined
) => {
  return isNotaInsuranceBillableItem(item) && !isaTaxItem(item);
};

type TransactionItemSubtypeKey = keyof typeof TransactionItemSubtypeEnum;

export const itemSubtypeRename: {
  [key in TransactionItemSubtypeKey]?: string;
} = {
  Adjustment: 'Discount',
  PatientPayment: 'Patient Payment',
  PatientRefund: 'Patient Refund',
  PatientWriteOff: 'Write Off',
  PatientCredit: 'Patient Credit',
  Supplement: 'Supplement or Supply',
  NoShow: 'No-Show',
};

export enum TransactionItemTypeEnum {
  Credit = 'Credit',
  Debit = 'Debit',
  Void = 'Void',
}

export type TransactionItemNoteType = {
  notes: string[];
};

export enum AdjustmentStructureEnum {
  DollarDiscount = '$ Discount',
  DollarOverride = '$ Override',
  PercentDiscount = '% Discount',
}
export enum TaxRateBasisEnum {
  Service = 's',
  Product = 'p',
  NoTax = 'z',
}

// sql/ddl/PatientTransactionItem.my.sql
export type PatientTransactionItemType = {
  id: number;
  // paymentId?: number | null;
  // payorReferenceId?: string | null;
  adjustment?: {
    percentOff?: number;
    structure: string;
    taxRate?: TaxRateBasisEnum;
  };
  adjustmentId?: number | null;
  amount: number | string | null;
  code?: string | null;
  codeSet?: string | null;
  createdAt?: Date | number | string;
  description?: string | null;
  diagnoses?: PatientTransactionItemDiagnosisType[] | null;
  /**
   * Display data for the transaction item.
   * - Can be a JSON string (when fetched from TEXT column in database)
   * - Can be a parsed object (after processing with normalizeDisplayData)
   * - Contains stableId and invoiceData for preserving custom invoice fields
   */
  displayData?: string | DisplayDataRecord | null;
  // finalAdjudicated?: number;
  // finalAdjudicatedPatientResponsibility?: number;
  // finalAdjudicatedByPayor?: {
  //   [key: string]: {
  //     finalAdjudicated: number;
  //     finalAdjudicatedPatientResponsibility: number;
  //   };
  // };
  groupId?: string | null;
  insuranceBillable?: boolean;
  invoiceId?: number | null;
  isDuplicate?: boolean;
  lineItemControlNumber?: string | null;
  locked?: boolean | false;
  method?: TransactionCreditMethodEnum | null;
  modifier1?: string | null;
  modifier2?: string | null;
  modifier3?: string | null;
  modifier4?: string | null;
  isDrugCode?: boolean;
  nationalDrugCode?: string | null;
  ndcUnits?: number | null;
  ndcUnitType?: string | null;
  notes?: string[] | null;
  nonBillable?: number;
  packageDate?: number | string | null;
  packageId?: number | null;
  parentPackageId?: number | null;
  patientPackageId?: number | null;
  patientTransactionId: number;
  paymentSource?: string;
  paymentToward?: string;
  payorId?: number | null; // TODO!!!! [Brian, these are crap. No such column.]
  payorName?: string | null; // TODO!!!! [Brian, these are crap. No such column.]
  // payors?: Payor[] | null; // TODO!!!! [Brian, these are crap. No such column.]
  productTaxRate?: number;
  salesTax?: boolean;
  serviceTaxRate?: number;
  siblings?: number[] | null;
  subtype: TransactionItemSubtypeEnum | null;
  supplementId?: number;
  taxAmount?: number | string | null;
  subtypeId?: number | null;
  type: TransactionItemTypeEnum | null;
  units: number | null;
  variableBilledAmount?: boolean;
};

// sql/ddl/PatientTransactionItemDiagnosis.my.sql
export type PatientTransactionItemDiagnosisType = {
  id: number;
  itemId: number;
  code: string;
  codeSet: CodeSets;
  description: string;
  ord: number;
};

// Path: sql/ddl/PatientTransaction.my.sql
/**
 * When one of these is being viewed in the UI, the items
 * are done separately.
 */
export type PatientTransactionSummary = {
  id: number;
  number?: string | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  type: TransactionTypeEnum;
  subtype?: PaymentType | null;
  balance?: number;
  startingBalance?: number;
  processingFee?: number;
  created: TransactionActionType;
  updated: TransactionActionType;
  patient?: PatientOnTransactionType;
  provider?: ProviderOnTransactionType;
  summary?: TransactionSummaryType;
  description?: string;
};

export type TransactionAppendItemsType = {
  services?: PatientTransactionItemType[];
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  courtesyBilling?: boolean;
  superBill?: boolean;
  newTransaction?: PatientTransaction;
};

export type PatientTransaction = PatientTransactionSummary & {
  // [key: string]: any;
  allocateToPatient?: boolean;
  allocatedFromClaimsAmount?: number;
  allowBillingPriorityChange?: boolean;
  amount?: number;
  assessmentCodes?: any[];
  billingProfileId?: number;
  caseSubtype?: CaseSubType | null;
  caseTypeId?: number | null;
  courtesyBilling?: boolean;
  disciplineId?: number | null;
  ePayBatchKey?: string;
  ePayRefNum?: string;
  finalized?: boolean;
  groupId?: number | null | undefined;
  hasAppointment?: boolean;
  appointmentDeletedAt?: number | null;
  hasEncounter?: boolean;
  encounterSigned?: boolean;
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  isBillingStarted?: boolean;
  isVoided?: boolean;
  items: PatientTransactionItemType[];
  locked?: boolean;
  nextRequestedEncounterDate?: number;
  patientBalance?: number | string;
  patientBilledAmount?: number | string;
  patientCaseTypeId?: number | null;
  patientSummary?: TransactionPatientBriefSummaryType | null;
  payments?: PatientPayment[];
  payorId?: number | null;
  payors?: Payor[];
  productTaxRate?: number;
  providerSummary?: any;
  providerCanChange?: boolean;
  purchases?: PatientTransaction[];
  receiptDetail?: ReceiptDetails;
  referenceNumber?: string;
  services?: PatientTransactionItemType[];
  servicesDirty?: boolean | null;
  serviceTaxRate?: number;
  status?: AppointmentStatuses | null;
  superBill?: boolean;
  taxDirty?: boolean | null;
  total?: number;
  transactionDate?: number | null;
  treatmentId?: number | null;
  tz?: string;
  verified?: number | null;
  verifiedBy?: string | null;
  // Keeps from having to get the Encounter just for the visitDate.
  visitDate?: number | null;
  appointmentClinicianId?: string | null;
  merged?: string | null;
  startTime?: number | null;
  paymentAmount?: number;
  hasBeenLocked?: boolean;
  hasDeductedInsurancePolicy?: boolean;
  paidForByBillingKey?: string | null;
  payorLineItems?: PayorLineItemType[];
  parentDisciplineId?: number | null;
  hasPostedClaims?: boolean;
  /** Lock state transition - set by savePatientTransaction, used by consolidatedTransactionUpdate for auto-837 */
  lockTransition?: {
    oldTransactionWasLocked: boolean;
    newTransactionIsLocked: boolean;
  };
};

export const TransactionItemTypeOptions = Object.keys(TransactionItemTypeEnum).map(key => ({
  text: key,
  value: key,
}));

/**
 * This is the raw row from the database table.
 */
export type PatientTransactionRowType = {
  id: number;
  number?: string | null;
  status?: string;
  taxDirty?: boolean;
  servicesDirty?: boolean | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  tz?: string;
  type?: TransactionTypeEnum;
  subtype?: PaymentType | null;
  balance?: number;
  startingBalance?: number;
  processingFee?: number;
  createdId?: string | null;
  updatedId?: string | null;
  patientId?: string | null;
  patientSummary?: string;
  providerId?: string;
  providerSummary?: string;
  summary?: string;
  createdAt?: number | null;
  updatedAt?: number | null;
  transactionDate: number;
  productTaxRate?: number;
  serviceTaxRate?: number;
  ePayRefNum?: string;
  ePayBatchKey?: string;
  referenceNumber?: string;
  description?: string;
  disciplineId?: number | null;
  treatmentId?: number | null;
  courtesyBilling?: boolean;
  superBill?: boolean;
  billingProfileId?: number;
  hasEncounter?: boolean;
  hasAppointment?: boolean;
  encounterSigned?: boolean;
  patientCaseTypeId?: number | null;
  caseTypeId?: number | null;
  caseSubtype?: CaseSubType | null;
  nextRequestedEncounterDate?: number;
  verified?: number | null;
  verifiedBy?: string | null;
  appointmentClinicianId?: string | null;
  merged?: string | null;
  hasBeenLocked?: boolean;
  paidForByBillingKey?: string | null;
};

export enum TransactionActivityType {
  Created = 'transaction-created',
  Updated = 'transaction-updated',
  PayorInvoiced = 'payor-invoiced',
}

export const getTransactionActivityDisplay = (
  activity: PatientTransactionActivity | undefined | null
) => {
  if (!activity) return 'No activity found';

  switch (activity.type) {
    case TransactionActivityType.Created:
      return 'Created';
    case TransactionActivityType.Updated:
      return 'Updated';
    case TransactionActivityType.PayorInvoiced:
      return 'Payor invoiced';
    default:
      return 'Unknown activity';
  }
};

export type PatientTransactionActivity = {
  id?: string;
  billingKey: string;
  clinicId: number;
  createdAt?: number;
  updated: TransactionActionType;
  description: string;
  type: TransactionActivityType;
  data?: {
    updates?: ACTIVITY_CHANGE_TYPE[];
    strings?: string[];
  };
  version: number; // A way to distinguish formatting changes.
};

/**
 * This is a special-case of a PaymentTransactionItem. Simplifies
 * the payment view.
 */
export type PatientPaymentType = {
  id: number;
  number?: string | null;
  billingKey: string;
  amount: number;
  processingFee: number;
  balance: number;
  paymentDate: string;
  isRefunded?: boolean;
  isPending?: boolean;
  isVoided?: boolean;
  description: string;
  type: TransactionTypeEnum;
  receiptDetail: ReceiptDetails;
  processor?: string;
  paidForByBillingKey?: string | null;
  paidByPatientId?: string;
  paidByPatientName?: string;
};

export type getPatientTransactionOptionsType = {
  rowOnly?: boolean;
  include?: {
    allowedAmounts?: boolean;
    assessmentCodes?: boolean;
    diagnoses?: boolean;
    eraIds?: boolean;
    insurance?: boolean;
    insuranceBillableItems?: boolean;
    invoices?: boolean;
    purchases?: boolean;
    payors?: boolean;
    services?: boolean;
    itemsOnPurchases?: boolean;
    payorLineItems?: boolean;
  };
};

export const GetPatientTransactionInsuranceOnlyOptions: getPatientTransactionOptionsType = {
  rowOnly: false,
  include: {
    allowedAmounts: false,
    assessmentCodes: false,
    diagnoses: false,
    eraIds: false,
    insurance: true,
    insuranceBillableItems: false,
    invoices: false,
    purchases: false,
    payors: false,
    services: false,
    itemsOnPurchases: false,
    payorLineItems: false,
  },
};

export type PatientTransactionSaveOptionsType = {
  engrams?: any;
  resaveBillingKey?: string;
  forcePersistSummary?: boolean;
  updatePatientDisplayName?: boolean;
  signatureChangeConfirmed?: boolean;
};

export type PatientTransactionSaveType = {
  allItemsOnConsolidatedTransaction?: PatientTransactionItemType[];
  bypassInsuranceRulesCheck?: boolean;
  bypassPostedClaimsCheck?: boolean;
  calledBy?: string;
  connection: Connection;
  ddb?: any;
  firstSlot?: boolean;
  forceUpdate?: boolean;
  getPatientTransactionOptions?: getPatientTransactionOptionsType;
  hasAccess: (feature: FeatureFlags) => boolean;
  insurances?: AppointmentInsuranceType[];
  magicEvent?: TransactionMagicEvent;
  mockError?: boolean;
  noShowAppointment?: boolean;
  previousSavedConsolidatedItems?: PatientTransactionItemType[];
  previousTransaction?: PatientTransaction; // We can pass an old one if we've just gotten it.
  primaryClinicianId?: string;
  purpose?: 'update' | undefined | null;
  savePatientBalance?: boolean;
  savingAsNew?: boolean;
  skipSaveIfNotDirty?: boolean;
  syncPatientSummary?: boolean;
  timed?: boolean;
  trace?: boolean;
  transaction?: PatientTransaction;
  updated: TransactionActionType;
};

/**
 * This type represents a transaction with associated invoices as returned from the database
 * in the transaction list API. It extends PatientTransaction with additional properties
 * needed for displaying transactions with invoice data.
 */
export type TransactionWithInvoices = PatientTransaction & {
  // Invoice-related properties
  invoices?: any[]; // Array of invoice objects
  total?: number; // Total amount across all invoices
  outstanding?: number; // Outstanding amount across all invoices
  itemColumn?: string[]; // For display in the UI
  displayName?: string; // For display in the UI
};

// export type PatientTransactionPayment = PatientTransactionItemType & {
//   paymentAmount: number;
//   transactionId: number;
//   summary?: { items: string[] };
// }
export type ReceiptDetails = {
  summary: {
    paymentTotal: number;
    total: {
      amount: number;
      label: string;
    };
    method: {
      type: string;
      label: string;
    };
    totalApplied: number;
    invoiceTotals: number;
    remainingBalance: number;
    paymentDate: string;
    processingFee: {
      label: string;
      amount: number;
    };
  };
  transactionPayments: TransactionPayment[];
  invoices: Partial<Invoice>[];
  isRefunded: boolean;
  isVoided: boolean;
  number?: string;
};

export type PatientOnTransactionType = {
  id: string;
  fname?: string;
  lname?: string;
  phone?: string;
  email?: string;
  displayName: string;
  profilePhoto?: string;
  dateOfBirth?: string;
  accountNumber?: string;
};
export class PatientTransactionClass implements PatientTransaction {
  id: number;
  number?: string | null;
  transactionDate?: number | null;
  status?: AppointmentStatuses | null;
  taxDirty?: boolean | null;
  servicesDirty?: boolean | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  balance?: number;
  startingBalance?: number;
  // processingFee?: number;
  type: TransactionTypeEnum;
  subtype: PaymentType | null;
  created: TransactionActionClass;
  updated: TransactionActionClass;
  patient?: PatientOnTransactionType;
  provider?: ProviderOnTransactionType;
  description?: string;
  items: any[]; // Can't stand it, had to go with any.
  summary: any;
  patientBalance?: number | string;
  patientBilledAmount?: number | string;
  courtesyBilling?: boolean;
  superBill?: boolean;
  billingProfileId?: number;
  onClick: (e: any, s: string) => void;
  tz?: string;
  patientCaseTypeId?: number | null;
  caseTypeId?: number | null;
  caseSubtype?: CaseSubType | null;
  nextRequestedEncounterDate?: number;
  verified?: number | null;
  verifiedBy?: string | null;
  appointmentClinicianId?: string | null;
  startTime?: number;
  hasBeenLocked?: boolean;

  constructor(
    row: PatientTransactionRowType,
    items: PatientTransactionItemType[],
    onClick?: (billingKey: string) => void
  ) {
    this.id = row.id;
    this.number = row.number;
    this.transactionDate = row.transactionDate;
    this.clinicId = row.clinicId;
    this.status = row.status as AppointmentStatuses;
    this.taxDirty = row.taxDirty;
    this.servicesDirty = row.servicesDirty;
    this.locationId = row.locationId;
    this.billingKey = row.billingKey;
    this.type = row.type as TransactionTypeEnum;
    this.subtype = row.subtype as PaymentType;
    this.balance = row.balance;
    this.startingBalance = row.startingBalance;
    this.nextRequestedEncounterDate = row.nextRequestedEncounterDate;
    this.verified = row.verified;
    this.verifiedBy = row.verifiedBy;
    // this.processingFee = row.processingFee;
    this.created = new TransactionActionClass({
      id: row.createdId,
      ts: row.createdAt,
    });
    this.updated = new TransactionActionClass({
      id: row.updatedId,
      ts: row.updatedAt,
    });
    this.patient =
      typeof row.patientSummary === 'string'
        ? ChiroUpJSON.parse(row.patientSummary)
        : row.patientSummary;
    this.provider =
      typeof row.providerSummary === 'string'
        ? ChiroUpJSON.parse(row.providerSummary)
        : row.providerSummary;
    this.courtesyBilling = row.courtesyBilling;
    this.superBill = row.superBill;
    this.items = items;
    this.billingProfileId = row.billingProfileId;

    if (onClick) {
      this.onClick = (_: any, s: string) => {
        onClick(s);
      };
    } else {
      this.onClick = () => {
        console.warn('no onClick');
      };
    }

    this.tz = row.tz;
    this.patientCaseTypeId = row.patientCaseTypeId;
    this.caseTypeId = row.caseTypeId;
    this.caseSubtype = row.caseSubtype;
    this.appointmentClinicianId = row.appointmentClinicianId;
    this.hasBeenLocked = row.hasBeenLocked;
  }

  static newRecord = ({
    billingKey,
    clinicId,
    locationId,
    type,
    created,
    provider,
    items,
    patient,
    courtesyBilling = false,
    superBill = false,
    tz = ChiroUpDayJsCommon.defaultTimezone,
    transactionDate = Date.now(),
  }: {
    billingKey: string;
    clinicId: number;
    locationId: number;
    type: TransactionTypeEnum;
    created: TransactionActionType;
    provider?: ProviderOnTransactionType;
    items?: PatientTransactionItemType[];
    patient?: PatientOnTransactionType;
    courtesyBilling?: boolean;
    superBill?: boolean;
    tz?: string;
    transactionDate?: number;
  }) => {
    return new this(
      {
        id: -1,
        number: null,
        billingKey,
        clinicId,
        locationId,
        balance: 0,
        courtesyBilling,
        superBill,
        type,
        createdAt: created.ts,
        createdId: created.id,
        updatedAt: null,
        updatedId: null,
        patientSummary: JSON.stringify(patient),
        providerSummary: JSON.stringify(provider),
        transactionDate,
        tz,
      },
      items || []
    );
  };

  static classFromObject = ({ transaction }: { transaction: PatientTransaction }) => {
    return new this(
      {
        id: transaction.id,
        number: transaction.number,
        transactionDate: transaction.transactionDate || 0,
        clinicId: transaction.clinicId,
        status: transaction.status as AppointmentStatuses,
        taxDirty: transaction.taxDirty ?? false,
        servicesDirty: transaction.servicesDirty ?? false,
        locationId: transaction.locationId,
        billingKey: transaction.billingKey,
        type: transaction.type,
        subtype: transaction.subtype,
        balance: transaction.balance,
        startingBalance: transaction.startingBalance,
        // processingFee: transaction.processingFee,
        createdId: transaction?.created?.id,
        createdAt: transaction?.created?.ts,
        updatedAt: transaction?.updated?.ts,
        updatedId: transaction?.updated?.id,
        patientSummary: JSON.stringify(transaction?.patient),
        providerSummary: JSON.stringify(transaction?.provider),
        courtesyBilling: transaction?.courtesyBilling,
        superBill: transaction?.superBill,
        tz: transaction?.tz,
        billingProfileId: transaction?.billingProfileId,
        nextRequestedEncounterDate: transaction?.nextRequestedEncounterDate,
        verified: transaction?.verified,
        verifiedBy: transaction?.verifiedBy,
        appointmentClinicianId: transaction?.appointmentClinicianId,
        hasBeenLocked: transaction?.hasBeenLocked,
      },
      transaction.items
    );
  };

  static initialFromPatientSummary = (
    patientSummary: TransactionPatientSummaryType,
    user: TransactionActionType,
    patient?: Patient
  ) => {
    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    const provider = {
      id: patientSummary.clinician?.id,
      displayName: patientSummary.clinician?.name,
      supervisedId: patientSummary.clinician?.supervisedId,
      supervisedName: patientSummary.clinician?.supervisedName,
    };

    return {
      id: -1,
      billingKey: patientSummary.billingKey,
      clinicId: patientSummary.clinicId,
      locationId: patientSummary.locationId || -1,
      transactionDate: patientSummary.appointmentDate,
      balance: 0,
      startingBalance: 0,
      // processingFee: 0,
      type: TransactionTypeEnum.Hybrid,
      subtype: null,
      created,
      updated: created,
      patient: {
        id: patientSummary.id,
        fname: patientSummary?.fname,
        lname: patientSummary?.lname,
        pname: patientSummary?.pname,
        displayName: patientSummary.displayName,
        phone: patientSummary.phone || patient?.phone,
        email: patientSummary.email || patient?.email,
      },
      items: [],
      provider: provider,
      courtesyBilling: false,
      superBill: false,
      caseSubtype: CaseSubType.ActiveTreatment,
    } as any; // PatientTransaction
  };

  static initialFromAppointment = (
    appt: AppointmentClass,
    user: TransactionActionType,
    patient?: Patient,
    courtesyBilling = false,
    superBill = false
  ) => {
    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    const provider = {
      id: appt.clinicianId,
      displayName: appt.displayValues.clinicianName,
    };

    return {
      id: -1,
      billingKey: appt.id,
      clinicId: appt.clinicId,
      locationId: appt.locationId,
      balance: 0,
      type: TransactionTypeEnum.Hybrid,
      created,
      courtesyBilling,
      superBill,
      updated: created,
      patient: {
        id: appt.patientId,
        displayName: appt.displayValues.patientName,
      },
      patientSummary: {
        id: appt.patientId,
        fname: patient?.fname,
        lname: patient?.lname,
        pname: patient?.pname,
        displayName: appt.displayValues.patientName,
        phone: appt.displayValues.patientPhone || patient?.phone,
        email: appt.displayValues.patientEmail || patient?.email,
      },
      items: [],
      provider: provider,
    } as any; // PatientTransaction
  };

  static initialFromPayment = (
    payment: PatientPayment,
    clinicId: number,
    user: TransactionActionType,
    credit: boolean,
    primaryTimezone: string,
    payorId?: number | null
  ) => {
    const billingKey = v4();

    const created = {
      id: user.id,
      ts: Date.now(),
      displayName: user.displayName || 'Unknown.',
    };

    let transactionDate = dayjs().tz(primaryTimezone).startOf('day').valueOf();

    if (typeof payment.paymentDate === 'string') {
      try {
        const convertedDate = dayjs(payment.paymentDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
        transactionDate = (
          createDayjs({
            datetime: convertedDate,
            timezone: 'UTC', // For date-only, we want UTC. No time please.
          }) as Dayjs
        )
          .startOf('day')
          .valueOf();
      } catch (e) {
        // It may be interesting to note that when date parses mm/dd/yyyy, it
        // is just a date. When it parses yyyy-mm-dd, it is a date and time so
        // we need to '00:00:00' at the end to make it leave it alone.
        try {
          transactionDate = dayjs
            .tz(`${payment.paymentDate} 00:00:00`, 'UTC')
            .startOf('day')
            .valueOf();
        } catch (e) {
          console.error(e);
          console.error(`Could not parse date: ${payment.paymentDate}.`);
        }
      }
    }

    const amount = Number(payment.amount) + Number(payment.processingFee || 0),
      items: any = [
        {
          id: -1,
          patientTransactionId: -1,
          description: `Initial payment${amount < 0 ? ' (rollback)' : ''}`,
          type: TransactionItemTypeEnum.Credit,
          subtype: TransactionItemSubtypeEnum.Payment,
          amount,
          units: 1,
          salesTax: 0,
        },
      ];

    const amountsPaid =
      payment.transactionPayments?.reduce((sum, tp) => sum + Number(tp.paymentAmount), 0) || 0;

    const final = {
      id: -1,
      number: null,
      billingKey,
      clinicId,
      description: payment.description,
      locationId: payment.locationId,
      // processingFee: payment.processingFee || 0,
      patient: !PayorPaymentTypesHash[payment.type as PaymentType]
        ? {
            id: payment.patientId,
            displayName: '',
          }
        : {
            id: payment.patientId,
            displayName: payment.description,
          },
      balance: payment.amount - amountsPaid,
      startingBalance: payment.amount + (payment.processingFee || 0),
      type: credit ? TransactionTypeEnum.Credit : TransactionTypeEnum.Payment,
      subtype: payment.type,
      amount: payment.amount,
      transactionDate,
      created,
      items,
      ePayRefNum: payment.ePayRefNum,
      ePayBatchKey: payment.ePayBatchKey,
      referenceNumber: payment?.referenceNumber ? String(payment?.referenceNumber) : null,
      tz: primaryTimezone,
      payorId,
    };

    return final as PatientTransaction;
  };
}

export const toTransactionItemSubtype = (
  subtype: TransactionItemSubtypeEnum | string,
  item: any
) => {
  switch (subtype) {
    case TransactionItemSubtypeEnum.Treatment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        units: 1,
        salesTax: item.salesTax,
        amount: item.amount,
        subtypeId: item.subtypeId || null,
      };
    }
    case TransactionItemSubtypeEnum.AddTreatment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        units: 1,
        salesTax: item.salesTax,
        amount: item.amount,
        subtypeId: item.subtypeId || null,
      };
    }
    case TransactionItemSubtypeEnum.PatientWriteOff: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Write-off',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        units: 1,
        amount: item.amount,
      };
    }
    case TransactionItemSubtypeEnum.Supplement: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.name,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.price,
        units: item.units || 1,
        groupId: item.groupId,
        salesTax: item.salesTax,
        supplementId: item.ID,
      };
    }
    case TransactionItemSubtypeEnum.AdditionalFee: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: item?.units ?? 1,
        groupId: item.groupId,
        salesTax: 0,
        packageId: item.packageId,
        parentPackageId: item?.parentPackageId,
      };
    }
    case TransactionItemSubtypeEnum.Service: {
      return {
        id: -1,
        patientTransactionId: -1,
        code: item.code,
        codeSet: item.codeSet,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.billedAmount,
        units: 1,
        modifier1: item.modifier1,
        modifier2: item.modifier2,
        modifier3: item.modifier3,
        modifier4: item.modifier4,
        locked: false,
        groupId: item.groupId,
        salesTax: item.salesTax,
        insuranceBillable: item.insuranceBillable,
      };
    }
    case TransactionItemSubtypeEnum.Payment: {
      const amount = Number(item.paymentAmount) || Number(item.amount);

      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: `Payment${amount < 0 ? ' (rollback)' : ''}`,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount,
        units: 1,
        salesTax: 0, //TODO Change this when I know more about tax
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        displayData: item.displayData ? JSON.stringify(item.displayData) : null,
        payorId: item.payorId || null,
      };
    }
    case TransactionItemSubtypeEnum.IncentivePayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: Number(item.paymentAmount) || Number(item.amount),
        units: 1,
        salesTax: 0, //TODO Change this when I know more about tax
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        displayData: item.displayData ? JSON.stringify(item.displayData) : null,
      };
    }
    case TransactionItemSubtypeEnum.Override: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item?.amount,
        units: 1,
        salesTax: 0,
        adjustment: item.adjustment || null,
        adjustmentId: item.adjustmentId || null,
      };
    }
    case TransactionItemSubtypeEnum.Adjustment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item?.amount,
        units: item?.units ?? 1,
        salesTax: 0,
        adjustment: item.adjustment ?? null,
        packageId: item?.packageId || null,
        parentPackageId: item?.parentPackageId || null,
        adjustmentId: item?.adjustmentId || null,
      };
    }
    case TransactionItemSubtypeEnum.NoShow: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.Canceled: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.PatientRefund: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.RefundUnappliedPayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.AppliedPayment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: `Payment${item.paymentAmount < 0 ? ' (rollback)' : ''}`,
        type: TransactionItemTypeEnum.Debit,
        subtype: TransactionItemSubtypeEnum.AppliedPayment,
        amount: item.paymentAmount,
        taxAmount: item.taxAmount,
        units: 1,
        salesTax: false,
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        createdAt: item.createdAt,
      };
    }
    case TransactionItemSubtypeEnum.PayorWriteOff: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Claims Write-off',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Credit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.PayorWriteOffReversal: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Claims Write-off Reversal',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.BalanceAllocatedToPatient: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Balance Allocated to Patient',
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
      };
    }
    case TransactionItemSubtypeEnum.Package: {
      return {
        id: item?.id ?? -1,
        patientTransactionId: -1,
        description: item.name,
        type: item.void ? TransactionItemTypeEnum.Void : TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.price,
        units: 1,
        salesTax: item.collectTax,
        packageId: item.packageId,
        packageDate: item.packageDate,
      };
    }
  }

  return item;
};

export class TransactionActionClass {
  id: string;
  ts?: number | null;
  displayName?: string;

  constructor(action: TransactionActionType) {
    this.id = action.id || '- unknown -';
    this.ts = action.ts;
    this.displayName = action.displayName || '- unknown -';
  }
}

/**
 * Service line items can only be added via the encounter coding
 * tool.
 */
const vals = Object.values(TransactionItemSubtypeEnum).filter(
  k => k !== 'Service' && k !== 'Payment'
);
export const TransactionItemSubtypeOptions = Object.keys(TransactionItemSubtypeEnum)
  .filter(key => key !== 'Service' && key !== 'Payment')
  .map((key, idx) => ({ text: vals[idx], value: key }));

/**
 * This is a SUBSET of the fields to build a transaction with the
 * extra details just for the patient. The duplicate fields in the
 * transaction are not included.
 */

export const TransactionCreditMethodOptions = Object.keys(TransactionCreditMethodEnum).map(key => ({
  text: key,
  value: key,
}));

// const vals2 = Object.values(TransactionItemSubtypeEnum).filter(
//   (k) =>
//     k !== 'Service' && k !== 'Canceled' && k !== 'No-Show' && k !== 'Payment',
// );
export const AdHocTransactionItemOptions = Object.keys(TransactionItemSubtypeEnum)
  .filter(key => {
    return key !== 'Service' && key !== 'Canceled' && key !== 'NoShow' && key !== 'Payment';
  })
  .map(key => {
    const keyTyped = key as keyof typeof TransactionItemSubtypeEnum;

    return {
      text: itemSubtypeRename[keyTyped] || TransactionItemSubtypeEnum[keyTyped],
      value: key,
    };
  });

export type TransactionList = {
  itemName: string;
  purchaseTotal: number;
  purchaseBalance: number;
  patientBalance: number;
  insurancePolicies: string[];
  invoiceStatuses: string[];
  billingKey: string;
};
